import React, { useCallback, useMemo } from "react";
import { CheckCircleIcon, ExclamationCircleIcon, XCircleIcon } from "@heroicons/react/24/outline";
import { XMarkIcon } from "@heroicons/react/20/solid";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { notificationMessageShape } from "@/utils/propTypeShapes";
import { NOTIFICATION_VARIANTS } from "@/utils/applicationConstants";
import { Button } from "@/components/button/button";
import { closeNotification as closeNotificationAction } from "@/redux/actions/notification";

export const Notification = React.forwardRef(({ id, message, notificationData }, ref) => {
  const dispatch = useDispatch();
  // notificationData contains the full message object { title, description, options }
  // message is the string title passed to enqueueSnackbar
  const data = useMemo(
    () => notificationData || { title: message, description: "", options: {} },
    [notificationData, message],
  );

  const closeNotification = useCallback(() => {
    dispatch(closeNotificationAction(id));
  }, [dispatch, id]);

  const handleButton = useCallback(() => {
    if (data.options?.buttonAction) data.options?.buttonAction();
  }, [data]);

  const icon = useMemo(() => {
    // TODO: Add Info type
    switch (data.options?.variant) {
      case NOTIFICATION_VARIANTS.SUCCESS:
        return <CheckCircleIcon className="size-8 bg-inherit text-green-400" aria-hidden="true" />;
      case NOTIFICATION_VARIANTS.ERROR:
        return <XCircleIcon className="size-8 bg-inherit text-red-800" aria-hidden="true" />;
      case NOTIFICATION_VARIANTS.WARNING:
        return <ExclamationCircleIcon className="size-8 bg-inherit text-yellow-400" aria-hidden="true" />;
      default:
        return <CheckCircleIcon className="size-6 bg-inherit text-green-400" aria-hidden="true" />;
    }
  }, [data]);

  return (
    <div
      ref={ref}
      aria-live="assertive"
      className="pointer-events-auto w-full max-w-sm overflow-hidden rounded-lg bg-white shadow-lg ring-1 ring-black/5 dark:bg-slate-800"
    >
      <div className="p-4">
        <div className="flex items-start">
          <div className="shrink-0">{icon}</div>
          <div className="ml-3 w-0 flex-1 pt-0.5">
            <p className="text-sm font-medium text-gray-900 dark:text-slate-100">{data.title}</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-slate-300">{data.description}</p>
            {data.options?.buttonAction && (
              <div className="mt-4 flex">
                <Button label={data.options?.buttonLabel} onClick={handleButton} />
              </div>
            )}
          </div>
          <div className="ml-4 flex shrink-0">
            <button
              type="button"
              onClick={closeNotification}
              className="inline-flex rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span className="sr-only">Close</span>
              <XMarkIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
});

Notification.propTypes = {
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  message: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  notificationData: notificationMessageShape,
};
